import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { parseLocalDate } from '../utils/dates';
import { useFacility } from '../contexts/FacilityContext';
import { fetchPatientById, fetchPatientInsurances, updatePatientInsurance, deletePatientInsurance, restorePatientInsurance, swapPatientInsurances, fetchPayers, createPayer, updatePatient, fetchPatientMrns, upsertPatientMrn, deletePatientMrn, fetchPatientPaymentPlans, restorePatient, revertPatientFieldToSource } from '../services/api';
import { TYPE_COLORS } from '../data/facilities';
import ActiveFacilityBanner from '../components/ActiveFacilityBanner';
import StatementModal from '../components/StatementModal';
import PaymentPlanModal from '../components/PaymentPlanModal';
import EligibilityModal from '../components/EligibilityModal';
import DeletePatientModal from '../components/DeletePatientModal';
import './PatientSummary.css';

const PLAN_FREQ_LABELS = { weekly: 'Weekly', bi_weekly: 'Bi-Weekly', monthly: 'Monthly' };

const DISPLAY_STATUS_LABELS = {
  pending:            'Pending',
  submitted:          'Submitted',
  secondary_pending:  'Secondary Pending',
  sent_to_secondary:  'Sent to Secondary',
  sent_to_tertiary:   'Sent to Tertiary',
  patient_balance:    'Patient Balance',
  paid:               'Paid',
  denied:             'Denied',
  voided:             'Voided',
};

// Raw claim lifecycle / clearinghouse status — distinct from the payment-oriented
// display_status above.
const CLAIM_STATUS_LABELS = {
  ready:        'Ready',
  generated:    'Generated',
  pending:      'Pending',
  accepted:     'Accepted',
  rejected:     'Rejected',
  voided:       'Voided',
  void_pending: 'Void Pending',
};

const PLAN_STATUS_COLORS = {
  active:    { bg: '#eff6ff', color: '#1d4ed8' },
  completed: { bg: '#f0fdf4', color: '#15803d' },
  defaulted: { bg: '#fef2f2', color: '#dc2626' },
  cancelled: { bg: '#f3f4f6', color: '#6b7280' },
};

function maskPhone(val) {
  const d = (val || '').replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// Demographic provenance (sync plan Step 5). Keys are the WIRE field names the
// server stores — address_line1 and postal_code, not the column names.
const PROVENANCE_FIELD_LABELS = {
  first_name: 'First name', last_name: 'Last name', date_of_birth: 'Date of birth', sex: 'Sex',
  address_line1: 'Street address', address_line2: 'Address line 2', city: 'City', state: 'State',
  postal_code: 'ZIP', phone: 'Phone', email: 'Email',
};

function formatProvenanceDate(value) {
  if (!value) return null;
  const date = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getAge(dob) {
  const birth = parseLocalDate(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function PatientSummary() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { facilities } = useFacility();

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Archive / restore state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState(null);

  // Insurance / payer state
  const [insurances, setInsurances] = useState([null, null, null]);
  const [activeInsTab, setActiveInsTab] = useState(0);
  const [editingIns, setEditingIns] = useState(null);
  const [insFormData, setInsFormData] = useState({});
  const [savingIns, setSavingIns] = useState(false);
  const [insError, setInsError] = useState(null);
  const [swappingIns, setSwappingIns] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removingIns, setRemovingIns] = useState(false);
  const [archivedInsurances, setArchivedInsurances] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [eligibilityIns, setEligibilityIns] = useState(null); // insurance row being checked
  const [showVoided, setShowVoided] = useState(false); // claims history: reveal voided claims (hidden by default)

  // Payment plans state
  const [paymentPlans, setPaymentPlans]         = useState([]);
  const [plansLoading, setPlansLoading]         = useState(false);
  const [showPaymentPlanModal, setShowPaymentPlanModal] = useState(false);

  // Account info edit state
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoFormData, setInfoFormData] = useState({});
  const [savingInfo, setSavingInfo] = useState(false);
  const [infoError, setInfoError] = useState(null);
  const [revertingField, setRevertingField] = useState(null);
  const [provenanceError, setProvenanceError] = useState(null);

  // Facility MRN state
  const [patientMrns, setPatientMrns] = useState([]);
  const [editingMrnFacilityId, setEditingMrnFacilityId] = useState(null);
  const [mrnEditValue, setMrnEditValue] = useState('');
  const [showAddMrn, setShowAddMrn] = useState(false);
  const [addMrnFacilityId, setAddMrnFacilityId] = useState('');
  const [addMrnValue, setAddMrnValue] = useState('');
  const [savingMrn, setSavingMrn] = useState(false);
  const [mrnError, setMrnError] = useState(null);

  // Payer combobox state
  const [payersList, setPayersList]           = useState([]);
  const [payerSearch, setPayerSearch]         = useState('');
  const [payerDropdownOpen, setPayerDropdownOpen] = useState(false);
  const [payerCreating, setPayerCreating]     = useState(false);

  const facilityNameMap = useMemo(
    () => Object.fromEntries(facilities.map((f) => [f.id, f.name])),
    [facilities]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchPatientById(id)
      .then((res) => {
        if (!cancelled) {
          setPageData(res.data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Patient not found');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!id) return;
    fetchPatientInsurances(id)
      .then((res) => {
        const rows = [null, null, null];
        const archived = [];
        res.data?.forEach((row) => {
          if (row.coverage_order >= 1 && row.coverage_order <= 3) rows[row.coverage_order - 1] = row;
          else if (row.coverage_order >= 90) archived.push(row);
        });
        setInsurances(rows);
        setArchivedInsurances(archived);
      })
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!id) return;
    fetchPatientMrns(id)
      .then((res) => setPatientMrns(res.data ?? []))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setPlansLoading(true);
    fetchPatientPaymentPlans(id)
      .then((res) => setPaymentPlans(res.data ?? []))
      .catch(() => {})
      .finally(() => setPlansLoading(false));
  }, [id]);

  const startEditInsurance = (tabIdx) => {
    const ins = insurances[tabIdx];
    loadPayers();
    setPayerSearch('');
    setPayerDropdownOpen(false);
    setInsFormData({
      insurance_payer_id:     ins?.insurance_payer_id     ?? null,
      insurance_provider:     ins?.insurance_provider     || '',
      policy_number:          ins?.policy_number          || '',
      group_number:           ins?.group_number           || '',
      group_name:             ins?.group_name             || '',
      health_plan_id:         ins?.health_plan_id         || '',
      insurance_type:         ins?.insurance_type         || '',
      subscriber_name:        ins?.subscriber_name        || '',
      subscriber_relation:    ins?.subscriber_relation    || 'Self',
      subscriber_dob:         ins?.subscriber_dob         ? String(ins.subscriber_dob).slice(0, 10) : '',
      subscriber_sex:         ins?.subscriber_sex         || '',
      insured_address:        ins?.insured_address        || '',
      insured_city:           ins?.insured_city           || '',
      insured_state:          ins?.insured_state          || '',
      insured_zip:            ins?.insured_zip            || '',
      insured_phone:          ins?.insured_phone          || '',
      effective_date:         ins?.effective_date         ? String(ins.effective_date).slice(0, 10)  : '',
      termination_date:       ins?.termination_date       ? String(ins.termination_date).slice(0, 10) : '',
      release_of_info:        ins?.release_of_info        || 'Y',
      assignment_of_benefits: ins?.assignment_of_benefits || 'Y',
      authorization_code:     ins?.authorization_code     || '',
      employer_name:          ins?.employer_name          || '',
      co_pay:                 ins?.co_pay != null              ? String(ins.co_pay) : '',
      notes:                  ins?.notes                  || '',
    });
    setEditingIns(tabIdx);
    setInsError(null);
  };

  const cancelEditInsurance = () => {
    setEditingIns(null);
    setInsError(null);
    setPayerDropdownOpen(false);
    setPayerSearch('');
  };

  const saveInsurance = async () => {
    if (!insFormData.insurance_payer_id && !insFormData.insurance_provider?.trim()) {
      setInsError('Select a payer from the list or create a new one.');
      return;
    }
    const selectedPayer = payersList.find((p) => p.payer_id === insFormData.insurance_payer_id);
    const isSelectedSelfPay = selectedPayer?.is_self_pay_payer === 1;
    if (!isSelectedSelfPay && !insFormData.policy_number?.trim()) {
      setInsError('Member / Policy ID is required.');
      return;
    }
    setSavingIns(true);
    setInsError(null);
    try {
      const payload = {
        ...insFormData,
        subscriber_dob:   insFormData.subscriber_dob   || null,
        effective_date:   insFormData.effective_date   || null,
        termination_date: insFormData.termination_date || null,
        co_pay:                insFormData.co_pay !== '' ? parseFloat(insFormData.co_pay) : null,
        insurance_payer_id: insFormData.insurance_payer_id || null,
      };
      await updatePatientInsurance(pageData.patient.id, editingIns + 1, payload);
      const res = await fetchPatientInsurances(id);
      const rows = [null, null, null];
      res.data?.forEach((row) => {
        if (row.coverage_order >= 1 && row.coverage_order <= 3) {
          rows[row.coverage_order - 1] = row;
        }
      });
      setInsurances(rows);
      setEditingIns(null);
    } catch (err) {
      setInsError(err?.response?.data?.error || 'Failed to save insurance record.');
    } finally {
      setSavingIns(false);
    }
  };

  const loadPayers = async () => {
    if (payersList.length > 0) return;
    try {
      const res = await fetchPayers();
      setPayersList(res.data ?? []);
    } catch { /* non-critical */ }
  };

  const refreshInsurances = async () => {
    const res = await fetchPatientInsurances(id);
    const rows = [null, null, null];
    const archived = [];
    res.data?.forEach((row) => {
      if (row.coverage_order >= 1 && row.coverage_order <= 3) rows[row.coverage_order - 1] = row;
      else if (row.coverage_order >= 90) archived.push(row);
    });
    setInsurances(rows);
    setArchivedInsurances(archived);
  };

  const swapInsurances = async (posA, posB) => {
    const insA = insurances[posA];
    const insB = insurances[posB];
    if (!insA && !insB) return;
    setSwappingIns(true);
    setInsError(null);
    try {
      const patientId = pageData.patient.id;
      const orderA = posA + 1;
      const orderB = posB + 1;

      // Single transactional call on the server — swaps both slots (or moves
      // one into an empty slot) atomically, so a failure can't leave the rows
      // half-reordered or stranded in the temp slot.
      await swapPatientInsurances(patientId, orderA, orderB);

      await refreshInsurances();
      setActiveInsTab(insA ? posB : posA);
    } catch (err) {
      setInsError(err?.response?.data?.error || 'Failed to reorder payers.');
    } finally {
      setSwappingIns(false);
    }
  };

  const removeInsurance = async () => {
    setRemovingIns(true);
    try {
      await deletePatientInsurance(pageData.patient.id, activeInsTab + 1);
      await refreshInsurances();
      setConfirmRemove(false);
    } catch (err) {
      setInsError(err?.response?.data?.error || 'Failed to archive insurance record.');
    } finally {
      setRemovingIns(false);
    }
  };

  const restoreInsurance = async (archivedOrder) => {
    setRestoringId(archivedOrder);
    try {
      const res = await restorePatientInsurance(pageData.patient.id, archivedOrder);
      await refreshInsurances();
      setActiveInsTab((res.targetSlot ?? 1) - 1);
    } catch (err) {
      setInsError(err?.response?.data?.error || 'Failed to restore insurance record.');
    } finally {
      setRestoringId(null);
    }
  };

  const startEditInfo = () => {
    const p = pageData.patient;
    setInfoFormData({
      first_name:     p.first_name      || '',
      last_name:      p.last_name       || '',
      date_of_birth:  p.date_of_birth   ? String(p.date_of_birth).slice(0, 10) : '',
      sex:            p.sex             || '',
      email:          p.email           || '',
      phone:          p.phone           || '',
      street_address: p.street_address  || '',
      address_line2:  p.address_line2   || '',
      city:           p.city            || '',
      state:          p.state           || '',
      zip:            p.zip             || '',
    });
    setEditingInfo(true);
    setInfoError(null);
  };

  const cancelEditInfo = () => {
    setEditingInfo(false);
    setInfoError(null);
  };

  const saveInfo = async () => {
    if (!infoFormData.first_name?.trim()) { setInfoError('First name is required.'); return; }
    if (!infoFormData.last_name?.trim())  { setInfoError('Last name is required.');  return; }
    setSavingInfo(true);
    setInfoError(null);
    try {
      const payload = {
        first_name:     infoFormData.first_name.trim(),
        last_name:      infoFormData.last_name.trim(),
        date_of_birth:  infoFormData.date_of_birth  || null,
        sex:            infoFormData.sex             || null,
        email:          infoFormData.email.trim()    || null,
        phone:          infoFormData.phone           || null,
        street_address: infoFormData.street_address.trim() || null,
        address_line2:  infoFormData.address_line2.trim() || null,
        city:           infoFormData.city.trim()     || null,
        state:          infoFormData.state.trim()    || null,
        zip:            infoFormData.zip.trim()      || null,
      };
      await updatePatient(pageData.patient.id, payload);
      setPageData((prev) => ({ ...prev, patient: { ...prev.patient, ...payload } }));
      setEditingInfo(false);
      await refreshProvenance();
    } catch (err) {
      setInfoError(err?.response?.data?.error || 'Failed to save patient information.');
    } finally {
      setSavingInfo(false);
    }
  };

  // The server decides which fields an edit changed, so re-read its provenance
  // rather than guessing client-side.
  const refreshProvenance = async () => {
    try {
      const res = await fetchPatientById(id);
      setPageData((prev) => ({ ...prev, patient: res.data.patient, field_provenance: res.data.field_provenance ?? [] }));
    } catch {
      // The save itself succeeded; the badges catch up on the next load.
    }
  };

  const revertFieldToSource = async (field) => {
    setRevertingField(field);
    setProvenanceError(null);
    try {
      await revertPatientFieldToSource(pageData.patient.id, field);
      await refreshProvenance();
    } catch (err) {
      setProvenanceError(err?.response?.data?.error || 'Failed to revert the field to its source.');
    } finally {
      setRevertingField(null);
    }
  };

  const saveMrn = async (facilityId) => {
    if (!mrnEditValue.trim()) { setMrnError('MRN cannot be empty.'); return; }
    setSavingMrn(true);
    setMrnError(null);
    try {
      await upsertPatientMrn(pageData.patient.id, facilityId, mrnEditValue.trim());
      const res = await fetchPatientMrns(id);
      setPatientMrns(res.data ?? []);
      setEditingMrnFacilityId(null);
    } catch (err) {
      setMrnError(err?.response?.data?.error || 'Failed to save MRN.');
    } finally {
      setSavingMrn(false);
    }
  };

  const addMrn = async () => {
    if (!addMrnFacilityId) { setMrnError('Select a facility.'); return; }
    if (!addMrnValue.trim()) { setMrnError('MRN cannot be empty.'); return; }
    setSavingMrn(true);
    setMrnError(null);
    try {
      await upsertPatientMrn(pageData.patient.id, addMrnFacilityId, addMrnValue.trim());
      const res = await fetchPatientMrns(id);
      setPatientMrns(res.data ?? []);
      setShowAddMrn(false);
      setAddMrnFacilityId('');
      setAddMrnValue('');
    } catch (err) {
      setMrnError(err?.response?.data?.error || 'Failed to add MRN.');
    } finally {
      setSavingMrn(false);
    }
  };

  const removeMrn = async (facilityId) => {
    setSavingMrn(true);
    setMrnError(null);
    try {
      await deletePatientMrn(pageData.patient.id, facilityId);
      setPatientMrns((prev) => prev.filter((m) => m.facility_id !== facilityId));
    } catch (err) {
      setMrnError(err?.response?.data?.error || 'Failed to remove MRN.');
    } finally {
      setSavingMrn(false);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    setRestoreError(null);
    try {
      await restorePatient(pageData.patient.id);
      setPageData((prev) => ({
        ...prev,
        patient: { ...prev.patient, archived_at: null, archived_by: null, archive_reason: null },
      }));
    } catch (err) {
      setRestoreError(err?.response?.data?.error || 'Failed to restore patient.');
    } finally {
      setRestoring(false);
    }
  };

  if (loading) {
    return (
      <div className="ps-page">
        <div className="ps-not-found">
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="ps-page">
        <div className="ps-not-found">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
          </svg>
          <h2>Patient Not Found</h2>
          <p>{error || 'The patient you are looking for does not exist.'}</p>
          <Link to="/patients">Back to Patients</Link>
        </div>
      </div>
    );
  }

  const { patient, facility, financials, claims: patientClaims } = pageData;
  const fullName = `${patient.first_name} ${patient.last_name}`;
  const age = getAge(patient.date_of_birth);
  const isArchived = !!patient.archived_at;

  const addressParts = [patient.street_address, patient.address_line2, patient.city, patient.state, patient.zip].filter(Boolean);

  // Facilities the patient is registered for: the home facility plus every
  // facility they have an MRN on file at. The home facility comes first; the
  // rest follow in MRN order. Resolve each id to the full facility record so we
  // get type/city/state, falling back to the leaner MRN row if the global
  // facilities list hasn't loaded a match.
  const facilityById = Object.fromEntries(facilities.map((f) => [f.id, f]));
  const patientFacilityIds = [];
  if (patient.facility_id != null) patientFacilityIds.push(patient.facility_id);
  patientMrns.forEach((m) => {
    if (!patientFacilityIds.includes(m.facility_id)) patientFacilityIds.push(m.facility_id);
  });
  const patientFacilities = patientFacilityIds
    .map((fid) => {
      const f = facilityById[fid];
      if (f) return f;
      const mrn = patientMrns.find((m) => m.facility_id === fid);
      return mrn ? { id: fid, name: mrn.facility_name, type: mrn.facility_type, city: '', state: '' } : null;
    })
    .filter(Boolean);

  const fieldProvenance = (pageData.field_provenance ?? []).filter((row) => row.field_name);

  const infoFields = [
    {
      label: 'Acct #',
      value: `#${String(patient.id).padStart(5, '0')}`,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" />
        </svg>
      ),
    },
    {
      label: 'Date of Birth',
      wrap: true,
      value: `${parseLocalDate(patient.date_of_birth).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} (${age} yrs)`,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      ),
    },
    {
      label: 'Sex',
      value: patient.sex === 'M' ? 'Male' : patient.sex === 'F' ? 'Female' : '—',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
        </svg>
      ),
    },
    {
      label: 'Email',
      value: patient.email,
      isEmail: true,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
        </svg>
      ),
    },
    {
      label: 'Phone',
      value: patient.phone || '—',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.11 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      ),
    },
    ...(addressParts.length > 0 ? [{
      label: 'Address',
      wrap: true,
      value: addressParts.join(', '),
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
        </svg>
      ),
    }] : []),
    {
      label: 'Insurance',
      value: patient.insurance_provider || '—',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      ),
    },
    {
      label: patientFacilities.length > 1 ? 'Facilities' : 'Facility',
      wrap: true,
      value: patientFacilities.length ? patientFacilities.map((f) => f.name).join(', ') : (facility?.name || '—'),
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
    },
  ];

  const fmt = (n) => '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Voided claims are hidden from the history by default and revealed via a toggle.
  const voidedClaimCount = patientClaims.filter((c) => c.claim_status === 'voided').length;
  const visibleClaims = showVoided
    ? patientClaims
    : patientClaims.filter((c) => c.claim_status !== 'voided');

  return (
    <div className="ps-page">
      <ActiveFacilityBanner />

      {/* Breadcrumb */}
      <div className="ps-breadcrumb">
        <Link to="/patients" className="ps-breadcrumb-link">Patients</Link>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span className="ps-breadcrumb-current">{fullName}</span>
      </div>

      {/* Hero Card */}
      <div className="ps-hero">
        <div className="ps-hero-top">
          <div className="ps-avatar">
            {patient.first_name[0]}{patient.last_name[0]}
          </div>
          <div className="ps-hero-info">
            <h1 className="ps-name">{fullName}</h1>
            <div className="ps-hero-meta">
              <span className="ps-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                {parseLocalDate(patient.date_of_birth).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} ({age} yrs)
              </span>
              {patient.insurance_provider && (
                <span className="ps-meta-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  {patient.insurance_provider}
                </span>
              )}
              {patient.phone && (
                <span className="ps-meta-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.11 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  {patient.phone}
                </span>
              )}
            </div>
          </div>
          <div className="ps-hero-actions">
            {facility && (
              <Link to={`/facility/${facility.id}`} className="ps-btn-facility">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                View Facility
              </Link>
            )}
            {!isArchived && (
              <button className="ps-btn-delete" onClick={() => setShowDeleteModal(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                Delete Patient
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Archived banner */}
      {isArchived && (
        <div className="ps-archived-banner">
          <div className="ps-archived-info">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" />
            </svg>
            <div>
              <strong>This patient is archived.</strong>
              <span>
                Archived on {parseLocalDate(patient.archived_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                {patient.archive_reason ? ` — “${patient.archive_reason}”` : ''}
              </span>
              {restoreError && <span className="ps-archived-error">{restoreError}</span>}
            </div>
          </div>
          <button className="ps-btn-restore" onClick={handleRestore} disabled={restoring}>
            {restoring ? 'Restoring…' : 'Restore Patient'}
          </button>
        </div>
      )}

      {/* Financial Overview */}
      <div className="ps-stats-row">
        <div className="ps-stat-card">
          <div className="ps-stat-icon billed">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <div className="ps-stat-content">
            <span className="ps-stat-value">{fmt(financials.total_billed)}</span>
            <span className="ps-stat-label">Total Billed</span>
          </div>
        </div>
        <div className="ps-stat-card">
          <div className="ps-stat-icon collected">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div className="ps-stat-content">
            {/* insurance collected + patient self-pay payments */}
            <span className="ps-stat-value">{fmt((financials.collected || 0) + (financials.patient_paid || 0))}</span>
            <span className="ps-stat-label">Collected</span>
          </div>
        </div>
        <div className="ps-stat-card">
          <div className="ps-stat-icon adjustment">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <div className="ps-stat-content">
            <span className="ps-stat-value">{fmt(financials.contractual_adj)}</span>
            <span className="ps-stat-label">Adjustments</span>
          </div>
        </div>
        <div className="ps-stat-card">
          <div className="ps-stat-icon patient-resp">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div className="ps-stat-content">
            {/* patient_outstanding = patient_responsibility − patient_paid (net balance owed) */}
            <span className="ps-stat-value">{fmt(financials.patient_outstanding)}</span>
            <span className="ps-stat-label">Pt. Outstanding</span>
          </div>
        </div>
        {financials.patient_credit > 0 && (
          <div className="ps-stat-card">
            <div className="ps-stat-icon credit">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
              </svg>
            </div>
            <div className="ps-stat-content">
              {/* patient_credit = patient_paid − patient_responsibility (overpayment owed back) */}
              <span className="ps-stat-value">{fmt(financials.patient_credit)}</span>
              <span className="ps-stat-label">Pt. Credit</span>
            </div>
          </div>
        )}
        {financials.pending_credit > 0 && (
          <div className="ps-stat-card" title="Paid on claims not generated yet. It is counted once the claim is generated, and cannot be applied elsewhere or refunded before then.">
            <div className="ps-stat-icon pending">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div className="ps-stat-content">
              {/* pending_credit = paid beyond the charge on claims not yet generated — not a credit */}
              <span className="ps-stat-value">{fmt(financials.pending_credit)}</span>
              <span className="ps-stat-label">Pending Coding</span>
            </div>
          </div>
        )}
        <div className="ps-stat-card">
          <div className="ps-stat-icon denied">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <div className="ps-stat-content">
            <span className="ps-stat-value">{fmt(financials.denied)}</span>
            <span className="ps-stat-label">Denied</span>
          </div>
        </div>
      </div>

      {/* Account Information */}
      <div className="ps-section">
        <div className="ps-section-header">
          <h2 className="ps-section-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
            </svg>
            Account Information
          </h2>
          {!editingInfo && (
            <button className="ps-payer-btn-edit" onClick={startEditInfo}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Edit
            </button>
          )}
        </div>

        {editingInfo ? (
          <div className="ps-payer-edit">
            <div className="ps-payer-section-label">Personal Information</div>
            <div className="ps-payer-edit-grid">
              <div className="ps-payer-edit-field">
                <label className="ps-payer-edit-label">First Name <span className="ps-payer-required">*</span></label>
                <input className="ps-payer-input" type="text" value={infoFormData.first_name} onChange={(e) => setInfoFormData((p) => ({ ...p, first_name: e.target.value }))} />
              </div>
              <div className="ps-payer-edit-field">
                <label className="ps-payer-edit-label">Last Name <span className="ps-payer-required">*</span></label>
                <input className="ps-payer-input" type="text" value={infoFormData.last_name} onChange={(e) => setInfoFormData((p) => ({ ...p, last_name: e.target.value }))} />
              </div>
              <div className="ps-payer-edit-field">
                <label className="ps-payer-edit-label">Date of Birth</label>
                <input className="ps-payer-input" type="date" value={infoFormData.date_of_birth} onChange={(e) => setInfoFormData((p) => ({ ...p, date_of_birth: e.target.value }))} />
              </div>
              <div className="ps-payer-edit-field">
                <label className="ps-payer-edit-label">Sex</label>
                <select className="ps-payer-select" value={infoFormData.sex} onChange={(e) => setInfoFormData((p) => ({ ...p, sex: e.target.value }))}>
                  <option value="">Select…</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </div>
            </div>

            <div className="ps-payer-section-label">Contact</div>
            <div className="ps-payer-edit-grid">
              <div className="ps-payer-edit-field ps-payer-span-2">
                <label className="ps-payer-edit-label">Email</label>
                <input className="ps-payer-input" type="email" value={infoFormData.email} onChange={(e) => setInfoFormData((p) => ({ ...p, email: e.target.value }))} placeholder="patient@example.com" />
              </div>
              <div className="ps-payer-edit-field">
                <label className="ps-payer-edit-label">Phone</label>
                <input className="ps-payer-input" type="tel" value={infoFormData.phone} onChange={(e) => setInfoFormData((p) => ({ ...p, phone: maskPhone(e.target.value) }))} placeholder="(555) 000-0000" />
              </div>
            </div>

            <div className="ps-payer-section-label">Address</div>
            <div className="ps-payer-edit-grid">
              <div className="ps-payer-edit-field ps-payer-span-2">
                <label className="ps-payer-edit-label">Street Address</label>
                <input className="ps-payer-input" type="text" value={infoFormData.street_address} onChange={(e) => setInfoFormData((p) => ({ ...p, street_address: e.target.value }))} placeholder="123 Main St" />
              </div>
              <div className="ps-payer-edit-field ps-payer-span-2">
                <label className="ps-payer-edit-label">Address Line 2</label>
                <input className="ps-payer-input" type="text" value={infoFormData.address_line2} onChange={(e) => setInfoFormData((p) => ({ ...p, address_line2: e.target.value }))} placeholder="Apt, suite, unit" />
              </div>
              <div className="ps-payer-edit-field">
                <label className="ps-payer-edit-label">City</label>
                <input className="ps-payer-input" type="text" value={infoFormData.city} onChange={(e) => setInfoFormData((p) => ({ ...p, city: e.target.value }))} />
              </div>
              <div className="ps-payer-edit-field">
                <label className="ps-payer-edit-label">State</label>
                <input className="ps-payer-input" type="text" value={infoFormData.state} onChange={(e) => setInfoFormData((p) => ({ ...p, state: e.target.value }))} maxLength={2} placeholder="TX" />
              </div>
              <div className="ps-payer-edit-field">
                <label className="ps-payer-edit-label">ZIP</label>
                <input className="ps-payer-input" type="text" value={infoFormData.zip} onChange={(e) => setInfoFormData((p) => ({ ...p, zip: e.target.value }))} maxLength={10} />
              </div>
            </div>

            {infoError && <div className="ps-payer-error">{infoError}</div>}
            <div className="ps-payer-edit-actions">
              <button className="ps-payer-btn-cancel" onClick={cancelEditInfo} disabled={savingInfo}>Cancel</button>
              <button className="ps-payer-btn-save" onClick={saveInfo} disabled={savingInfo}>
                {savingInfo ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <div className="ps-info-grid">
            {infoFields.map((field) => (
              <div key={field.label} className="ps-info-item">
                <div className="ps-info-icon">{field.icon}</div>
                <div className="ps-info-content">
                  <span className="ps-info-label">{field.label}</span>
                  <span className={`ps-info-value${field.wrap ? ' ps-info-value--wrap' : ''}`}>
                    {field.isEmail
                      ? (field.value ? <a href={`mailto:${field.value}`}>{field.value}</a> : '—')
                      : field.value}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Per-field provenance (sync plan Step 5, N6): who set each demographic field.
            A human edit holds the field against source updates until it is reverted. */}
        {!editingInfo && fieldProvenance.length > 0 && (
          <div className="ps-provenance">
            <div className="ps-provenance-title">Field sources</div>
            {provenanceError && <div className="ps-payer-error">{provenanceError}</div>}
            <ul className="ps-provenance-list">
              {fieldProvenance.map((row) => {
                const label = PROVENANCE_FIELD_LABELS[row.field_name] || row.field_name;
                const isHumanEdit = row.source === 'coder';
                const editor = row.updated_by_name || row.updated_by_username || 'a former user';
                return (
                  <li key={row.field_name} className="ps-provenance-item">
                    <span className="ps-provenance-field">{label}</span>
                    {isHumanEdit ? (
                      <span className="ps-provenance-badge ps-provenance-badge--edited">
                        Edited by {editor} on {formatProvenanceDate(row.updated_at)}
                      </span>
                    ) : (
                      <span className="ps-provenance-badge ps-provenance-badge--source">From source</span>
                    )}
                    <span className="ps-provenance-detail">
                      {row.source_updated_at
                        ? <>Source currently sends <strong>{row.source_value ?? '(blank)'}</strong> (as of {formatProvenanceDate(row.source_updated_at)})</>
                        : 'Source has not sent this field yet'}
                    </span>
                    {isHumanEdit && (
                      <button
                        className="ps-payer-btn-edit ps-provenance-revert"
                        onClick={() => revertFieldToSource(row.field_name)}
                        disabled={revertingField !== null}
                        title={row.source_updated_at
                          ? `Replace the edited value with what the source sends (${row.source_value ?? 'blank'})`
                          : 'Remove the edit; the next value the source sends will apply'}
                      >
                        {revertingField === row.field_name ? 'Reverting…' : 'Revert to source'}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {/* Facility MRNs */}
      {(() => {
        const existingIds = new Set(patientMrns.map((m) => m.facility_id));
        const availableFacilities = facilities.filter((f) => !existingIds.has(f.id));
        return (
          <div className="ps-section">
            <div className="ps-section-header">
              <h2 className="ps-section-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" /><line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" />
                </svg>
                Facility MRNs
              </h2>
              {!showAddMrn && availableFacilities.length > 0 && (
                <button
                  className="ps-payer-btn-edit"
                  onClick={() => { setShowAddMrn(true); setAddMrnFacilityId(''); setAddMrnValue(''); setMrnError(null); }}
                  disabled={savingMrn}
                >
                  + Add
                </button>
              )}
            </div>

            {patientMrns.length === 0 && !showAddMrn ? (
              <div className="ps-mrn-empty">No facility MRNs on file</div>
            ) : (
              <div className="ps-mrn-list">
                {patientMrns.map((mrn) => {
                  const isEditing = editingMrnFacilityId === mrn.facility_id;
                  return (
                    <div key={mrn.facility_id} className="ps-mrn-row">
                      <span className="ps-mrn-facility-dot" style={{ background: TYPE_COLORS[mrn.facility_type] || '#171363' }} />
                      <span className="ps-mrn-facility-name">{mrn.facility_name}</span>
                      {isEditing ? (
                        <>
                          <input
                            className="ps-mrn-input"
                            type="text"
                            value={mrnEditValue}
                            onChange={(e) => setMrnEditValue(e.target.value)}
                            placeholder="Enter MRN"
                            autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') saveMrn(mrn.facility_id); if (e.key === 'Escape') { setEditingMrnFacilityId(null); setMrnError(null); } }}
                          />
                          <div className="ps-mrn-actions">
                            <button className="ps-payer-btn-save ps-mrn-btn-sm" onClick={() => saveMrn(mrn.facility_id)} disabled={savingMrn}>{savingMrn ? 'Saving…' : 'Save'}</button>
                            <button className="ps-payer-btn-cancel ps-mrn-btn-sm" onClick={() => { setEditingMrnFacilityId(null); setMrnError(null); }}>Cancel</button>
                          </div>
                        </>
                      ) : (
                        <>
                          <span className="ps-mrn-value">{mrn.mrn}</span>
                          <div className="ps-mrn-actions">
                            <button
                              className="ps-payer-btn-edit"
                              onClick={() => { setEditingMrnFacilityId(mrn.facility_id); setMrnEditValue(mrn.mrn); setMrnError(null); }}
                              disabled={savingMrn}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                              Edit
                            </button>
                            <button className="ps-mrn-btn-delete" onClick={() => removeMrn(mrn.facility_id)} disabled={savingMrn} title="Remove MRN">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}

                {showAddMrn && (
                  <div className="ps-mrn-add-row">
                    <select
                      className="ps-mrn-select"
                      value={addMrnFacilityId}
                      onChange={(e) => setAddMrnFacilityId(e.target.value)}
                      autoFocus
                    >
                      <option value="">Select facility…</option>
                      {availableFacilities.map((f) => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                    <input
                      className="ps-mrn-input ps-mrn-input-add"
                      type="text"
                      value={addMrnValue}
                      onChange={(e) => setAddMrnValue(e.target.value)}
                      placeholder="Enter MRN"
                      onKeyDown={(e) => { if (e.key === 'Enter') addMrn(); if (e.key === 'Escape') { setShowAddMrn(false); setMrnError(null); } }}
                    />
                    <button className="ps-payer-btn-save ps-mrn-btn-sm" onClick={addMrn} disabled={savingMrn}>{savingMrn ? 'Saving…' : 'Save'}</button>
                    <button className="ps-payer-btn-cancel ps-mrn-btn-sm" onClick={() => { setShowAddMrn(false); setMrnError(null); }}>Cancel</button>
                  </div>
                )}
              </div>
            )}
            {mrnError && <div className="ps-payer-error" style={{ margin: '0 24px 14px' }}>{mrnError}</div>}
          </div>
        );
      })()}

      {/* Insurance / Payers */}
      <div className="ps-section">
        <div className="ps-section-header">
          <h2 className="ps-section-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Insurance / Payers
          </h2>
        </div>

        <div className="ps-payer-tabs">
          {['Primary', 'Secondary', 'Tertiary'].map((label, i) => (
            <button
              key={i}
              className={`ps-payer-tab ${activeInsTab === i ? 'active' : ''}`}
              onClick={() => {
                setActiveInsTab(i);
                setConfirmRemove(false);
                if (editingIns !== null && editingIns !== i) cancelEditInsurance();
              }}
            >
              {label}
              {insurances[i] && <span className="ps-payer-tab-dot" />}
            </button>
          ))}
        </div>

        {(() => {
          const ins = insurances[activeInsTab];
          const isEditing = editingIns === activeInsTab;
          const LABELS = ['Primary', 'Secondary', 'Tertiary'];
          const swapTargets = [0, 1, 2].filter((i) => i !== activeInsTab);
          const fmtDate = (val) => val ? new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

          // Sync plan Step 11 (B): a policy with a `source_system` is maintained by
          // that facility's source system. Its next coverage message OVERWRITES the
          // policy and subscriber fields, including an edit made here — unlike the
          // patient's own demographics, which keep a human edit. Said BEFORE the
          // biller edits, on the view and on the form, not discovered days later.
          const sourceNotice = ins?.source_system ? (
            <div className="ps-source-maintained" role="note">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12a9 9 0 1 1-3-6.7" /><polyline points="21 3 21 9 15 9" />
              </svg>
              <span>
                <strong>Source-maintained</strong> — kept in sync from {ins.source_system}. The next update from that
                system replaces the member, plan and subscriber details, including any edit made here. Correct them in
                the source system.
              </span>
            </div>
          ) : null;

          if (isEditing) {
            const upd = (field) => (e) => setInsFormData((p) => ({ ...p, [field]: e.target.value }));
            const selectedPayer = payersList.find((p) => p.payer_id === insFormData.insurance_payer_id);
            const isSelectedSelfPay = selectedPayer?.is_self_pay_payer === 1;
            return (
              <div className="ps-payer-edit">
                {sourceNotice}
                {/* Plan Information */}
                <div className="ps-payer-section-label">Plan Information</div>
                <div className="ps-payer-edit-grid">
                  {/* Payer combobox — search the master payers list or create inline */}
                  <div className="ps-payer-edit-field ps-payer-span-2">
                    <label className="ps-payer-edit-label">Payer <span className="ps-payer-required">*</span></label>
                    {insFormData.insurance_payer_id ? (
                      <>
                        <div className="ps-payer-selected">
                          <span className="ps-payer-selected-name">{insFormData.insurance_provider}</span>
                          {!isSelectedSelfPay && selectedPayer?.payer_code && (
                            <span className="ps-payer-selected-code">Payer ID: {selectedPayer.payer_code}</span>
                          )}
                          <button
                            type="button"
                            className="ps-payer-selected-change"
                            onClick={() => {
                              setInsFormData((p) => ({ ...p, insurance_payer_id: null, insurance_provider: '' }));
                              setPayerSearch('');
                              setPayerDropdownOpen(true);
                            }}
                          >
                            Change
                          </button>
                        </div>
                        {!isSelectedSelfPay && !selectedPayer?.payer_code && (
                          <p className="ps-payer-field-warning">
                            This payer has no Claim.MD Payer ID on file — claims can’t be submitted until
                            one is added on the Payers page (or entered below as an override).
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="ps-payer-combobox">
                        <input
                          className="ps-payer-input"
                          type="text"
                          value={payerSearch}
                          placeholder="Search payers…"
                          autoComplete="off"
                          onFocus={() => setPayerDropdownOpen(true)}
                          onChange={(e) => {
                            setPayerSearch(e.target.value);
                            setPayerDropdownOpen(true);
                          }}
                          onBlur={() => setTimeout(() => setPayerDropdownOpen(false), 150)}
                        />
                        {payerDropdownOpen && (
                          <div className="ps-payer-dropdown">
                            {(() => {
                              const selfPayPayer  = payersList.find((py) => py.is_self_pay_payer);
                              const regularPayers = payersList.filter((py) =>
                                !py.is_self_pay_payer &&
                                (!payerSearch.trim() || py.name.toLowerCase().includes(payerSearch.toLowerCase()))
                              );
                              const showSelfPay = selfPayPayer && (
                                !payerSearch.trim() ||
                                selfPayPayer.name.toLowerCase().includes(payerSearch.toLowerCase())
                              );
                              const selectPayer = (py) => {
                                setInsFormData((p) => ({
                                  ...p,
                                  insurance_payer_id: py.payer_id,
                                  insurance_provider: py.name,
                                }));
                                setPayerSearch('');
                                setPayerDropdownOpen(false);
                              };
                              return (
                                <>
                                  {showSelfPay && (
                                    <button
                                      key={selfPayPayer.payer_id}
                                      type="button"
                                      className="ps-payer-dropdown-item ps-payer-dropdown-selfpay"
                                      onMouseDown={() => selectPayer(selfPayPayer)}
                                    >
                                      {selfPayPayer.name}
                                      <span className="ps-payer-dropdown-code">Uninsured</span>
                                    </button>
                                  )}
                                  {showSelfPay && regularPayers.length > 0 && (
                                    <div className="ps-payer-dropdown-divider" />
                                  )}
                                  {regularPayers.map((py) => (
                                    <button
                                      key={py.payer_id}
                                      type="button"
                                      className="ps-payer-dropdown-item"
                                      onMouseDown={() => selectPayer(py)}
                                    >
                                      {py.name}
                                      {py.payer_code && (
                                        <span className="ps-payer-dropdown-code">{py.payer_code}</span>
                                      )}
                                    </button>
                                  ))}
                                </>
                              );
                            })()}
                            {payerSearch.trim() &&
                              !payersList.some(
                                (py) => py.name.toLowerCase() === payerSearch.toLowerCase()
                              ) && (
                                <button
                                  type="button"
                                  className="ps-payer-dropdown-create"
                                  disabled={payerCreating}
                                  onMouseDown={async () => {
                                    setPayerCreating(true);
                                    try {
                                      const res = await createPayer({ name: payerSearch.trim() });
                                      const newPayer = res.data;
                                      setPayersList((prev) => [...prev, newPayer].sort((a, b) => a.name.localeCompare(b.name)));
                                      setInsFormData((p) => ({
                                        ...p,
                                        insurance_payer_id: newPayer.payer_id,
                                        insurance_provider: newPayer.name,
                                      }));
                                      setPayerSearch('');
                                      setPayerDropdownOpen(false);
                                    } catch (err) {
                                      setInsError(err?.response?.data?.error || 'Failed to create payer.');
                                    } finally {
                                      setPayerCreating(false);
                                    }
                                  }}
                                >
                                  {payerCreating ? 'Creating…' : `+ Add "${payerSearch.trim()}" as new payer`}
                                </button>
                              )}
                            {!payerSearch.trim() && payersList.length === 0 && (
                              <div className="ps-payer-dropdown-empty">No payers on file — type a name to create one</div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {!isSelectedSelfPay && (
                    <div className="ps-payer-edit-field">
                      <label className="ps-payer-edit-label">Member / Policy ID <span className="ps-payer-required">*</span></label>
                      <input className="ps-payer-input" type="text" value={insFormData.policy_number} onChange={upd('policy_number')} placeholder="Member ID or Policy #" />
                    </div>
                  )}
                  <div className="ps-payer-edit-field">
                    <label className="ps-payer-edit-label">Group Number</label>
                    <input className="ps-payer-input" type="text" value={insFormData.group_number} onChange={upd('group_number')} />
                  </div>
                  <div className="ps-payer-edit-field">
                    <label className="ps-payer-edit-label">Group Name</label>
                    <input className="ps-payer-input" type="text" value={insFormData.group_name} onChange={upd('group_name')} placeholder="Group plan name" />
                  </div>
                  <div className="ps-payer-edit-field">
                    <label className="ps-payer-edit-label">Payer ID override</label>
                    <input
                      className="ps-payer-input"
                      type="text"
                      value={insFormData.health_plan_id}
                      onChange={upd('health_plan_id')}
                      placeholder={selectedPayer?.payer_code ? `Defaults to ${selectedPayer.payer_code}` : 'Payer / plan ID'}
                    />
                    <span className="ps-payer-field-hint">
                      Leave blank to use the selected payer’s Claim.MD Payer ID. Only set this to override it for this patient.
                    </span>
                  </div>
                  <div className="ps-payer-edit-field">
                    <label className="ps-payer-edit-label">Insurance Type</label>
                    <select className="ps-payer-select" value={insFormData.insurance_type} onChange={upd('insurance_type')}>
                      <option value="">Select…</option>
                      <option value="medicare">Medicare</option>
                      <option value="medicaid">Medicaid</option>
                      <option value="commercial">Commercial</option>
                      <option value="tricare">TRICARE</option>
                      <option value="champva">CHAMPVA</option>
                      <option value="group">Group Health Plan</option>
                      <option value="feca">FECA</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="ps-payer-edit-field">
                    <label className="ps-payer-edit-label">Effective Date</label>
                    <input className="ps-payer-input" type="date" value={insFormData.effective_date} onChange={upd('effective_date')} />
                  </div>
                  <div className="ps-payer-edit-field">
                    <label className="ps-payer-edit-label">Termination Date</label>
                    <input className="ps-payer-input" type="date" value={insFormData.termination_date} onChange={upd('termination_date')} />
                  </div>
                </div>

                {/* Subscriber / Insured */}
                <div className="ps-payer-section-label">Subscriber / Insured</div>
                <div className="ps-payer-edit-grid">
                  <div className="ps-payer-edit-field ps-payer-span-2">
                    <label className="ps-payer-edit-label">Subscriber Name</label>
                    <input className="ps-payer-input" type="text" value={insFormData.subscriber_name} onChange={upd('subscriber_name')} placeholder="Last, First (if not patient)" />
                  </div>
                  <div className="ps-payer-edit-field">
                    <label className="ps-payer-edit-label">Relationship</label>
                    <select className="ps-payer-select" value={insFormData.subscriber_relation} onChange={upd('subscriber_relation')}>
                      <option value="Self">Self</option>
                      <option value="Spouse">Spouse</option>
                      <option value="Child">Child</option>
                      <option value="Employee">Employee</option>
                      <option value="Life Partner">Life Partner</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="ps-payer-edit-field">
                    <label className="ps-payer-edit-label">Subscriber DOB</label>
                    <input className="ps-payer-input" type="date" value={insFormData.subscriber_dob} onChange={upd('subscriber_dob')} />
                  </div>
                  <div className="ps-payer-edit-field">
                    <label className="ps-payer-edit-label">Subscriber Sex</label>
                    <select className="ps-payer-select" value={insFormData.subscriber_sex} onChange={upd('subscriber_sex')}>
                      <option value="">Select…</option>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                    </select>
                  </div>
                  <div className="ps-payer-edit-field">
                    <label className="ps-payer-edit-label">Employer Name</label>
                    <input className="ps-payer-input" type="text" value={insFormData.employer_name} onChange={upd('employer_name')} placeholder="Employer or school" />
                  </div>
                </div>

                {/* Insured Address */}
                <div className="ps-payer-section-label">Insured Address</div>
                <div className="ps-payer-edit-grid">
                  <div className="ps-payer-edit-field ps-payer-span-2">
                    <label className="ps-payer-edit-label">Street Address</label>
                    <input className="ps-payer-input" type="text" value={insFormData.insured_address} onChange={upd('insured_address')} placeholder="123 Main St" />
                  </div>
                  <div className="ps-payer-edit-field">
                    <label className="ps-payer-edit-label">City</label>
                    <input className="ps-payer-input" type="text" value={insFormData.insured_city} onChange={upd('insured_city')} />
                  </div>
                  <div className="ps-payer-edit-field">
                    <label className="ps-payer-edit-label">State</label>
                    <input className="ps-payer-input" type="text" value={insFormData.insured_state} onChange={upd('insured_state')} maxLength={2} placeholder="TX" />
                  </div>
                  <div className="ps-payer-edit-field">
                    <label className="ps-payer-edit-label">ZIP</label>
                    <input className="ps-payer-input" type="text" value={insFormData.insured_zip} onChange={upd('insured_zip')} maxLength={10} />
                  </div>
                  <div className="ps-payer-edit-field">
                    <label className="ps-payer-edit-label">Phone</label>
                    <input className="ps-payer-input" type="tel" value={insFormData.insured_phone} onChange={(e) => setInsFormData((p) => ({ ...p, insured_phone: maskPhone(e.target.value) }))} placeholder="(555) 000-0000" />
                  </div>
                </div>

                {/* Billing Settings */}
                <div className="ps-payer-section-label">Billing Settings</div>
                <div className="ps-payer-edit-grid">
                  <div className="ps-payer-edit-field">
                    <label className="ps-payer-edit-label">Release of Info</label>
                    <select className="ps-payer-select" value={insFormData.release_of_info} onChange={upd('release_of_info')}>
                      <option value="Y">Y – Yes</option>
                      <option value="R">R – Restricted</option>
                      <option value="N">N – No</option>
                    </select>
                  </div>
                  <div className="ps-payer-edit-field">
                    <label className="ps-payer-edit-label">Assignment of Benefits</label>
                    <select className="ps-payer-select" value={insFormData.assignment_of_benefits} onChange={upd('assignment_of_benefits')}>
                      <option value="Y">Y – Yes</option>
                      <option value="N">N – No</option>
                    </select>
                  </div>
                  <div className="ps-payer-edit-field">
                    <label className="ps-payer-edit-label">Authorization Code</label>
                    <input className="ps-payer-input" type="text" value={insFormData.authorization_code} onChange={upd('authorization_code')} placeholder="Pre-auth / treatment auth code" />
                  </div>
                  <div className="ps-payer-edit-field">
                    <label className="ps-payer-edit-label">Co-pay</label>
                    <input className="ps-payer-input" type="number" step="0.01" min="0" value={insFormData.co_pay} onChange={upd('co_pay')} placeholder="0.00" />
                  </div>
                  <div className="ps-payer-edit-field ps-payer-span-4">
                    <label className="ps-payer-edit-label">Notes</label>
                    <textarea className="ps-payer-textarea" value={insFormData.notes} onChange={upd('notes')} rows={2} placeholder="Additional notes about this payer…" />
                  </div>
                </div>

                {insError && <div className="ps-payer-error">{insError}</div>}
                <div className="ps-payer-edit-actions">
                  <button className="ps-payer-btn-cancel" onClick={cancelEditInsurance} disabled={savingIns}>Cancel</button>
                  <button className="ps-payer-btn-save" onClick={saveInsurance} disabled={savingIns}>
                    {savingIns ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            );
          }

          if (!ins) {
            return (
              <div className="ps-payer-empty">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <p>No {LABELS[activeInsTab].toLowerCase()} insurance on file</p>
                <button className="ps-payer-btn-add" onClick={() => startEditInsurance(activeInsTab)}>
                  + Add {LABELS[activeInsTab]} Insurance
                </button>
              </div>
            );
          }

          // Self-pay patients show a compact "Uninsured" card instead of the full
          // policy/subscriber grid — there's no plan data to display.
          if (ins.is_self_pay_payer) {
            return (
              <div className="ps-payer-view">
                <div className="ps-selfpay-card">
                  <div className="ps-selfpay-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 12V22H4V12"/><path d="M22 7H2v5h20V7z"/><path d="M12 22V7"/>
                      <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
                    </svg>
                  </div>
                  <div className="ps-selfpay-body">
                    <span className="ps-selfpay-label">Self Pay — Uninsured</span>
                    <span className="ps-selfpay-sub">
                      Patient is responsible for the full billed amount.
                      {ins.effective_date && ` Effective ${fmtDate(ins.effective_date)}.`}
                    </span>
                    {ins.notes && <span className="ps-selfpay-notes">{ins.notes}</span>}
                  </div>
                </div>
                <div className="ps-payer-view-actions">
                  <button className="ps-payer-btn-edit" onClick={() => startEditInsurance(activeInsTab)} disabled={swappingIns}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    Edit
                  </button>
                  <div className="ps-payer-divider" />
                  <button className="ps-payer-btn-remove" onClick={() => setConfirmRemove(true)} disabled={swappingIns}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                    Remove
                  </button>
                </div>
                {confirmRemove && (
                  <div className="ps-payer-confirm-remove">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>This will archive the Self Pay designation. Past claims are unaffected.</span>
                    <div className="ps-payer-confirm-actions">
                      <button className="ps-payer-btn-cancel" onClick={() => setConfirmRemove(false)} disabled={removingIns}>Cancel</button>
                      <button className="ps-payer-btn-remove-confirm" onClick={removeInsurance} disabled={removingIns}>
                        {removingIns ? 'Archiving…' : 'Archive'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          }

          return (
            <div className="ps-payer-view">
              {sourceNotice}
              <div className="ps-payer-view-grid">
                {/* Plan Information */}
                <div className="ps-payer-view-item ps-payer-span-2">
                  <span className="ps-payer-view-label">Payer</span>
                  <span className="ps-payer-view-value">
                    {ins.insurance_provider || '—'}
                    {ins.insurance_payer_id && (
                      <span className="ps-payer-linked-badge">linked</span>
                    )}
                  </span>
                </div>
                {ins.plan_name && (
                  <div className="ps-payer-view-item">
                    <span className="ps-payer-view-label">Plan</span>
                    <span className="ps-payer-view-value">{ins.plan_name}</span>
                  </div>
                )}
                <div className="ps-payer-view-item">
                  <span className="ps-payer-view-label">Member / Policy ID</span>
                  <span className="ps-payer-view-value">{ins.policy_number || '—'}</span>
                </div>
                <div className="ps-payer-view-item">
                  <span className="ps-payer-view-label">Group Number</span>
                  <span className="ps-payer-view-value">{ins.group_number || '—'}</span>
                </div>
                <div className="ps-payer-view-item">
                  <span className="ps-payer-view-label">Group Name</span>
                  <span className="ps-payer-view-value">{ins.group_name || '—'}</span>
                </div>
                <div className="ps-payer-view-item">
                  <span className="ps-payer-view-label">Payer ID</span>
                  <span className="ps-payer-view-value">{ins.health_plan_id || ins.payer_code || '—'}</span>
                </div>
                <div className="ps-payer-view-item">
                  <span className="ps-payer-view-label">Insurance Type</span>
                  <span className="ps-payer-view-value" style={{ textTransform: 'capitalize' }}>{ins.insurance_type || '—'}</span>
                </div>
                <div className="ps-payer-view-item">
                  <span className="ps-payer-view-label">Effective Date</span>
                  <span className="ps-payer-view-value">{fmtDate(ins.effective_date)}</span>
                </div>
                <div className="ps-payer-view-item">
                  <span className="ps-payer-view-label">Termination Date</span>
                  <span className="ps-payer-view-value">{fmtDate(ins.termination_date)}</span>
                </div>

                {/* Subscriber */}
                <div className="ps-payer-view-item ps-payer-span-2">
                  <span className="ps-payer-view-label">Subscriber Name</span>
                  <span className="ps-payer-view-value">{ins.subscriber_name || '—'}</span>
                </div>
                <div className="ps-payer-view-item">
                  <span className="ps-payer-view-label">Relationship</span>
                  <span className="ps-payer-view-value">{ins.subscriber_relation || '—'}</span>
                </div>
                <div className="ps-payer-view-item">
                  <span className="ps-payer-view-label">Subscriber DOB</span>
                  <span className="ps-payer-view-value">{fmtDate(ins.subscriber_dob)}</span>
                </div>
                <div className="ps-payer-view-item">
                  <span className="ps-payer-view-label">Subscriber Sex</span>
                  <span className="ps-payer-view-value">{ins.subscriber_sex === 'M' ? 'Male' : ins.subscriber_sex === 'F' ? 'Female' : ins.subscriber_sex || '—'}</span>
                </div>
                <div className="ps-payer-view-item">
                  <span className="ps-payer-view-label">Employer Name</span>
                  <span className="ps-payer-view-value">{ins.employer_name || '—'}</span>
                </div>

                {/* Insured Address */}
                {(ins.insured_address || ins.insured_city) && (
                  <div className="ps-payer-view-item ps-payer-span-2">
                    <span className="ps-payer-view-label">Insured Address</span>
                    <span className="ps-payer-view-value">
                      {[ins.insured_address, ins.insured_city, ins.insured_state, ins.insured_zip].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}
                {ins.insured_phone && (
                  <div className="ps-payer-view-item">
                    <span className="ps-payer-view-label">Insured Phone</span>
                    <span className="ps-payer-view-value">{ins.insured_phone}</span>
                  </div>
                )}

                {/* Billing Settings */}
                <div className="ps-payer-view-item">
                  <span className="ps-payer-view-label">Release of Info</span>
                  <span className="ps-payer-view-value">{ins.release_of_info === 'Y' ? 'Yes' : ins.release_of_info === 'R' ? 'Restricted' : ins.release_of_info === 'N' ? 'No' : ins.release_of_info || '—'}</span>
                </div>
                <div className="ps-payer-view-item">
                  <span className="ps-payer-view-label">Assignment of Benefits</span>
                  <span className="ps-payer-view-value">{ins.assignment_of_benefits === 'Y' ? 'Yes' : ins.assignment_of_benefits === 'N' ? 'No' : ins.assignment_of_benefits || '—'}</span>
                </div>
                {ins.authorization_code && (
                  <div className="ps-payer-view-item">
                    <span className="ps-payer-view-label">Auth Code</span>
                    <span className="ps-payer-view-value">{ins.authorization_code}</span>
                  </div>
                )}
                {ins.co_pay != null && (
                  <div className="ps-payer-view-item">
                    <span className="ps-payer-view-label">Co-pay</span>
                    <span className="ps-payer-view-value">${parseFloat(ins.co_pay).toFixed(2)}</span>
                  </div>
                )}
                {ins.notes && (
                  <div className="ps-payer-view-item ps-payer-span-4">
                    <span className="ps-payer-view-label">Notes</span>
                    <span className="ps-payer-view-value">{ins.notes}</span>
                  </div>
                )}
              </div>

              {confirmRemove ? (
                <div className="ps-payer-confirm-remove">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>This payer will be archived. Past claims that used this payer are unaffected.</span>
                  <div className="ps-payer-confirm-actions">
                    <button className="ps-payer-btn-cancel" onClick={() => setConfirmRemove(false)} disabled={removingIns}>Cancel</button>
                    <button className="ps-payer-btn-remove-confirm" onClick={removeInsurance} disabled={removingIns}>
                      {removingIns ? 'Archiving…' : 'Archive'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="ps-payer-view-actions">
                  <button className="ps-payer-btn-eligibility" onClick={() => setEligibilityIns(ins)} disabled={swappingIns} title="Run a real-time eligibility & benefits check with the payer">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 12l2 2 4-4" /><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                    Check Eligibility
                  </button>
                  <div className="ps-payer-divider" />
                  <button className="ps-payer-btn-edit" onClick={() => startEditInsurance(activeInsTab)} disabled={swappingIns}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    Edit
                  </button>
                  <div className="ps-payer-divider" />
                  {swapTargets.map((targetIdx) => (
                    <button
                      key={targetIdx}
                      className="ps-payer-btn-swap"
                      onClick={() => swapInsurances(activeInsTab, targetIdx)}
                      disabled={swappingIns}
                      title={insurances[targetIdx] ? `Swap with ${LABELS[targetIdx]}` : `Move to ${LABELS[targetIdx]}`}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M7 16V4m0 0L3 8m4-4l4 4" /><path d="M17 8v12m0 0l4-4m-4 4l-4-4" />
                      </svg>
                      {insurances[targetIdx] ? `Swap w/ ${LABELS[targetIdx]}` : `Move to ${LABELS[targetIdx]}`}
                    </button>
                  ))}
                  <div className="ps-payer-divider" />
                  <button className="ps-payer-btn-remove" onClick={() => setConfirmRemove(true)} disabled={swappingIns}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                    Remove
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Archived Payers */}
      {archivedInsurances.length > 0 && (
        <div className="ps-section">
          <button
            className="ps-archived-toggle"
            onClick={() => setShowArchived((v) => !v)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" />
            </svg>
            Archived Payers
            <span className="ps-archived-count">{archivedInsurances.length}</span>
            <svg
              className={`ps-archived-chevron ${showArchived ? 'open' : ''}`}
              width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {showArchived && (
            <div className="ps-archived-list">
              {archivedInsurances.map((ins) => {
                const fmtDate = (val) => val ? new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
                const isRestoring = restoringId === ins.coverage_order;
                const activeSlotsFull = insurances.every(Boolean);
                return (
                  <div key={ins.patient_insurance_id} className="ps-archived-item">
                    <div className="ps-archived-fields">
                      <div className="ps-archived-field">
                        <span className="ps-payer-view-label">Plan Name</span>
                        <span className="ps-payer-view-value">{ins.insurance_provider || '—'}</span>
                      </div>
                      <div className="ps-archived-field">
                        <span className="ps-payer-view-label">Member / Policy ID</span>
                        <span className="ps-payer-view-value">{ins.policy_number || '—'}</span>
                      </div>
                      <div className="ps-archived-field">
                        <span className="ps-payer-view-label">Group Number</span>
                        <span className="ps-payer-view-value">{ins.group_number || '—'}</span>
                      </div>
                      <div className="ps-archived-field">
                        <span className="ps-payer-view-label">Subscriber</span>
                        <span className="ps-payer-view-value">{ins.subscriber_name || '—'}</span>
                      </div>
                      <div className="ps-archived-field">
                        <span className="ps-payer-view-label">Effective</span>
                        <span className="ps-payer-view-value">{fmtDate(ins.effective_date)}</span>
                      </div>
                      <div className="ps-archived-field">
                        <span className="ps-payer-view-label">Terminated</span>
                        <span className="ps-payer-view-value">{fmtDate(ins.termination_date)}</span>
                      </div>
                    </div>
                    <button
                      className="ps-archived-restore-btn"
                      onClick={() => restoreInsurance(ins.coverage_order)}
                      disabled={isRestoring || activeSlotsFull}
                      title={activeSlotsFull ? 'All 3 active slots are occupied — archive one first' : 'Restore to first available active slot'}
                    >
                      {isRestoring ? 'Restoring…' : (
                        <>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" />
                          </svg>
                          Restore
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Facilities */}
      {patientFacilities.length > 0 && (
        <div className="ps-section">
          <div className="ps-section-header">
            <h2 className="ps-section-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              {patientFacilities.length > 1 ? 'Facilities' : 'Facility'}
            </h2>
          </div>
          <div className="ps-facility-list">
            {patientFacilities.map((f) => (
              <Link key={f.id} to={`/facility/${f.id}`} className="ps-facility-card">
                <span className="ps-facility-dot" style={{ background: TYPE_COLORS[f.type] || '#171363' }} />
                <span className="ps-facility-name">{f.name}</span>
                {f.id === patient.facility_id && <span className="ps-facility-home">Home</span>}
                <span className="ps-facility-type" style={{ background: TYPE_COLORS[f.type] || '#171363' }}>
                  {f.type}
                </span>
                {(f.city || f.state) && (
                  <span className="ps-facility-location">{[f.city, f.state].filter(Boolean).join(', ')}</span>
                )}
                <svg className="ps-facility-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Payment Plans */}
      <div className="ps-section">
        <div className="ps-section-header">
          <h2 className="ps-section-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            Payment Plans
          </h2>
          <span className="ps-section-count">{paymentPlans.length}</span>
          <button className="ps-btn-create-plan" onClick={() => setShowPaymentPlanModal(true)}>
            + Create Plan
          </button>
        </div>

        {plansLoading ? (
          <div style={{ padding: '28px', textAlign: 'center', color: '#9ca3af', fontSize: '0.88rem' }}>Loading…</div>
        ) : paymentPlans.length === 0 ? (
          <div className="ps-plans-empty">
            <p>No payment plans on file</p>
            <button className="ps-payer-btn-add" onClick={() => setShowPaymentPlanModal(true)}>
              + Create Payment Plan
            </button>
          </div>
        ) : (
          <div className="ps-plans-list">
            {paymentPlans.map((plan) => {
              const pct = plan.total_amount > 0
                ? Math.round((parseFloat(plan.amount_paid) / parseFloat(plan.total_amount)) * 100)
                : 0;
              const statusStyle = PLAN_STATUS_COLORS[plan.status] ?? PLAN_STATUS_COLORS.cancelled;
              const paidCount = (plan.installments ?? []).filter((i) => i.status === 'paid').length;
              const totalCount = (plan.installments ?? []).length;
              return (
                <div key={plan.payment_plan_id} className="ps-plan-card">
                  <div className="ps-plan-header">
                    <span className="ps-plan-number">{plan.plan_number}</span>
                    <span className="ps-plan-status" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                      {plan.status.charAt(0).toUpperCase() + plan.status.slice(1)}
                    </span>
                    <span className="ps-plan-freq">{PLAN_FREQ_LABELS[plan.frequency] ?? plan.frequency}</span>
                    <Link to="/revenue/payments?section=plans" className="ps-plan-view-link">View in Payments →</Link>
                  </div>
                  <div className="ps-plan-progress">
                    <div className="ps-plan-bar">
                      <div className="ps-plan-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="ps-plan-pct">{pct}%</span>
                  </div>
                  <div className="ps-plan-stats">
                    <div className="ps-plan-stat">
                      <span className="ps-plan-stat-label">Total</span>
                      <span className="ps-plan-stat-val">{fmt(plan.total_amount)}</span>
                    </div>
                    <div className="ps-plan-stat">
                      <span className="ps-plan-stat-label">Paid</span>
                      <span className="ps-plan-stat-val" style={{ color: '#15803d' }}>{fmt(plan.amount_paid)}</span>
                    </div>
                    <div className="ps-plan-stat">
                      <span className="ps-plan-stat-label">Remaining</span>
                      <span className="ps-plan-stat-val" style={{ color: '#171363', fontWeight: 700 }}>{fmt(plan.remaining_balance)}</span>
                    </div>
                    <div className="ps-plan-stat">
                      <span className="ps-plan-stat-label">Installment</span>
                      <span className="ps-plan-stat-val">{fmt(plan.installment_amount)}</span>
                    </div>
                    {totalCount > 0 && (
                      <div className="ps-plan-stat">
                        <span className="ps-plan-stat-label">Installments</span>
                        <span className="ps-plan-stat-val">{paidCount} / {totalCount} paid</span>
                      </div>
                    )}
                    {plan.next_due_date && plan.status === 'active' && (
                      <div className="ps-plan-stat">
                        <span className="ps-plan-stat-label">Next Due</span>
                        <span className="ps-plan-stat-val">
                          {new Date(plan.next_due_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Statement Modal */}
      {showStatementModal && (
        <StatementModal
          patient={patient}
          onClose={() => setShowStatementModal(false)}
        />
      )}

      {/* Eligibility Modal */}
      {eligibilityIns && (
        <EligibilityModal
          patient={patient}
          insurance={eligibilityIns}
          facility={facility}
          onClose={() => setEligibilityIns(null)}
        />
      )}

      {/* Payment Plan Modal */}
      {showPaymentPlanModal && (
        <PaymentPlanModal
          patient={patient}
          onClose={() => setShowPaymentPlanModal(false)}
          onSaved={() => {
            setShowPaymentPlanModal(false);
            setPlansLoading(true);
            fetchPatientPaymentPlans(id)
              .then((res) => setPaymentPlans(res.data ?? []))
              .catch(() => {})
              .finally(() => setPlansLoading(false));
          }}
        />
      )}

      {showDeleteModal && (
        <DeletePatientModal
          patientId={patient.id}
          patientName={fullName}
          financials={financials}
          onClose={() => setShowDeleteModal(false)}
          onDeleted={() => {
            setShowDeleteModal(false);
            navigate('/patients');
          }}
        />
      )}

      {/* Claims History */}
      <div className="ps-section">
        <div className="ps-section-header">
          <h2 className="ps-section-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            Claims History
          </h2>
          <span className="ps-section-count">{visibleClaims.length} {visibleClaims.length === 1 ? 'claim' : 'claims'}</span>
          <div className="ps-claims-header-actions">
            {voidedClaimCount > 0 && (
              <button
                className={`ps-claims-toggle ${showVoided ? 'active' : ''}`}
                onClick={() => setShowVoided((v) => !v)}
                title={showVoided ? 'Hide voided claims' : 'Show voided claims'}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                {showVoided ? 'Hide voided' : `Show voided (${voidedClaimCount})`}
              </button>
            )}
            <button
              className="ps-btn-statement"
              onClick={() => setShowStatementModal(true)}
              title="Generate a patient statement for printing"
            >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 6 2 18 2 18 9"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            Generate Statement
            </button>
          </div>
        </div>

        {visibleClaims.length === 0 ? (
          <div className="ps-claims-empty">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
            </svg>
            <p>
              {patientClaims.length === 0
                ? 'No claims found for this patient'
                : `All ${voidedClaimCount} ${voidedClaimCount === 1 ? 'claim is' : 'claims are'} voided — use “Show voided” to view ${voidedClaimCount === 1 ? 'it' : 'them'}`}
            </p>
          </div>
        ) : (
          <table className="ps-claims-table">
            <thead>
              <tr>
                <th>Claim #</th>
                <th>Service Date</th>
                <th>Provider</th>
                <th>CPT Codes</th>
                <th className="num">Amount</th>
                <th>Claim Status</th>
                <th>Payment Status</th>
                <th>Facility</th>
              </tr>
            </thead>
            <tbody>
              {visibleClaims.map((claim) => (
                <tr
                  key={claim.id}
                  onClick={() => navigate(`/claims/${claim.id}`)}
                  style={{ cursor: 'pointer' }}
                  className={claim.claim_status === 'voided' ? 'ps-claim-row-voided' : undefined}
                >
                  <td className="ps-claim-number">{claim.claim_number}</td>
                  <td className="ps-claim-date">
                    {parseLocalDate(claim.service_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>
                  <td>{claim.provider}</td>
                  <td>
                    <div className="ps-claim-cpt">
                      {claim.cpt_codes.map((c) => (
                        <span key={c} className="ps-cpt-tag">{c}</span>
                      ))}
                    </div>
                  </td>
                  <td className="ps-claim-amount">{fmt(claim.amount)}</td>
                  <td>
                    <span className={`ps-status ps-status-${claim.claim_status}`}>
                      {CLAIM_STATUS_LABELS[claim.claim_status] || claim.claim_status}
                    </span>
                  </td>
                  <td>
                    <span className={`ps-status ps-status-${claim.display_status || claim.status}`}>
                      {DISPLAY_STATUS_LABELS[claim.display_status] || claim.status.charAt(0).toUpperCase() + claim.status.slice(1)}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                    {facilityNameMap[claim.facility_id] || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default PatientSummary;
